import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo
} from "react";
import { FaTimes } from "react-icons/fa";
import { TbArrowBarLeft } from "react-icons/tb";
import { Button } from "reactstrap";
import SubMenu from "./SubMenu";
import Logo from "./../../../assets/images/logonew.png";
import LogoIcon from "./../../../assets/images/logo-sm.png";
import { Link, useNavigate } from "react-router-dom";
import Header from "../header/Header";
import useBackendAccessApi from "../../../hooks/useBackendAccessApi";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { setIsMenuActive } from "../../../redux/dashboardSlice";
import "./skeleton.css";
import {
  SidebarSkeleton,
  SidebarLogoSkeleton
} from "../../skeletons/SidebarSkeleton";
import { DASHBOARD, INVOICE } from "../../../route/paths";
import "./../../../assets/scss/common.scss";
import ErrorBoundary from "../../error-boundary";
import { fundTapLogo } from "../../../locales/en";
import { AiOutlineDashboard } from "react-icons/ai";
import {
  DASHBOARD_MENU,
  DASHBOARD_MENU_ID
} from "../../../utils/constants/appConstants";
import { getNavItems } from "../../../redux/userManagementSlice";

const SideBar = ({ children }) => {
  const initialRender = useRef(true);
  const [isOpen, setIsOpen] = useState(false);

  const { isMenuActive } = useSelector((state) => state.dashboardTable);
  const [isHovered, setIsHovered] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    window.matchMedia("(min-width: 990px)").matches
  );

  const navItemLoading = useSelector(
    (state) => state.userManagement.navItemsLoading
  );
  const navItems = useSelector((state) => state.userManagement.navItems);
  const { IsBackendPermission } = useBackendAccessApi();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isBackendPermissionAccess = useCallback(async () => {
    try {
      const response = await IsBackendPermission();
      const isPermission = response.data.data;
      if (!isPermission) {
        navigate(INVOICE);
      }
    } catch {
      navigate(INVOICE);
    }
  }, [IsBackendPermission, navigate]);

  const filteredNavItems = useMemo(() => {
    return navItems
      ?.filter((item) => item.isVisibleSidebar)
      ?.map((item) => ({
        ...item,
        children: item.children?.filter((child) => child.isVisibleSidebar) || []
      }));
  }, [navItems]);

  useEffect(() => {
    dispatch(getNavItems());
  }, []);

  useEffect(() => {
    if (initialRender.current) {
      isBackendPermissionAccess();
      initialRender.current = false;
    }

    const handleResize = () => {
      setIsDesktop(window.matchMedia("(min-width: 990px)").matches);
    };

    const handleClickOutside = (event) => {
      if (
        isMenuActive &&
        window.innerWidth <= 992 &&
        !event.target.closest(".sidebar") &&
        !event.target.closest(".bars")
      ) {
        setIsOpen(false);
        dispatch(setIsMenuActive(false));
      }
    };

    // Scroll control based on isMenuActive
    if (isMenuActive && window.innerWidth <= 992) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }

    window.addEventListener("resize", handleResize);
    document.addEventListener("click", handleClickOutside);

    // Cleanup function
    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("click", handleClickOutside);
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isBackendPermissionAccess, isMenuActive]);

  const toggle = () => {
    setIsOpen(!isOpen);
    dispatch(setIsMenuActive(!isMenuActive));
  };

  const handleClickMenuItemRemoveSidbar = () => {
    setIsOpen(false);
    dispatch(setIsMenuActive(false));
  };

  const handleMouseEnter = () => {
    if (isMenuActive) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <>
      <div className={`admin-header ${isMenuActive ? "active-sidebar" : ""}`}>
        <Header />
      </div>
      <div className="container-fluid main-wrap">
        <div
          className={`bars ${isMenuActive ? "close" : ""} ${isOpen ? "left75" : ""}`}
        >
          <Button onClick={toggle}>
            <TbArrowBarLeft />
          </Button>
        </div>
        <div
          className={`sidebar ${isMenuActive ? "active-sidebar" : ""} ${isHovered ? "active-hover-menu" : ""}`}
          onMouseEnter={isDesktop ? handleMouseEnter : undefined}
          onMouseLeave={isDesktop ? handleMouseLeave : undefined}
        >
          <div className="top_section">
            <div className="logo">
              <div className={`${isOpen ? "d-none" : "d-block"}`}>
                {navItemLoading ? (
                  <SidebarLogoSkeleton />
                ) : (
                  <Link to={INVOICE}>
                    <img src={Logo} alt={fundTapLogo} />
                  </Link>
                )}
              </div>
              <div className={`logo-icon ${isOpen ? "d-block" : "d-none"}`}>
                <Link to={INVOICE}>
                  <img src={LogoIcon} alt={fundTapLogo} />
                </Link>
              </div>
            </div>
            <Button onClick={toggle}>
              <FaTimes />
            </Button>
          </div>

          <ul
            className={`sidebar-menu ${isMenuActive ? "active-sidebar" : ""} ${isHovered ? "active-hover-menu" : ""}`}
            onMouseEnter={isDesktop ? handleMouseEnter : undefined}
            onMouseLeave={isDesktop ? handleMouseLeave : undefined}
          >
            {navItemLoading ? (
              <ErrorBoundary fallback={<SidebarSkeleton />}>
                <SidebarSkeleton />
              </ErrorBoundary>
            ) : (
              <>
                {!filteredNavItems?.some(
                  (item) =>
                    item.id === DASHBOARD_MENU_ID &&
                    item.name === DASHBOARD_MENU
                ) && (
                  <li>
                    <Link to={DASHBOARD}>
                      <span className="d-flex menu-item">
                        <AiOutlineDashboard />
                        <span className="left-10">{DASHBOARD_MENU}</span>
                      </span>
                    </Link>
                  </li>
                )}

                {filteredNavItems
                  ?.filter((item) => item.isActive)
                  ?.map((item) => (
                    <li key={item.id}>
                      <SubMenu
                        item={item}
                        handleClickMenuItemRemoveSidbar={
                          handleClickMenuItemRemoveSidbar
                        }
                      />
                    </li>
                  ))}
              </>
            )}
          </ul>
        </div>
        <div
          className={`content-outer-wrapper ${isMenuActive ? "active-sidebar" : ""}`}
        >
          {children}
        </div>
      </div>
    </>
  );
};
SideBar.propTypes = {
  children: PropTypes.node
};

export default SideBar;
